import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { CheckCircle2, AlertTriangle } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { AreaChart, Legend, type ChartSeries, type Day } from '@/components/viz/Charts'
import { DayBars, ErrorNote, GrowthFrame, Kpi, ShareBar, StatusPill, metricCells, money, num, pct, ratio, shortDate, useSelection } from '../components/GrowthFrame'
import { PlanList } from './StrategyPage'
import { useOverview, useStrategy } from '../hooks/useGrowth'
import type { Overview } from '../api/growthApi'

const SPEND = 'var(--viz-sessions)'
const REVENUE = 'var(--viz-active)'

/** ROAS on a half-dial with the break-even mark; above or below is said in words, not only color. */
function RoasGauge({ roas, breakEven, spend, currency, value }: { roas: number; breakEven: number; spend: number; currency: string; value: number }) {
  const max = Math.max(breakEven * 2, roas * 1.15, 1)
  const [shown, setShown] = useState(0)
  useEffect(() => { const f = requestAnimationFrame(() => setShown(Math.min(roas, max))); return () => cancelAnimationFrame(f) }, [roas, max])
  const r = 70
  const point = (v: number) => {
    const a = Math.PI * (1 - v / max)
    return [90 + r * Math.cos(a), 86 - r * Math.sin(a)] as const
  }
  const arc = (v: number) => { const [x, y] = point(v); return `M20,86 A${r},${r} 0 0 1 ${x},${y}` }
  const [bx, by] = point(breakEven)
  const above = spend > 0 && roas >= breakEven
  const Icon = above ? CheckCircle2 : AlertTriangle
  const color = above ? 'var(--viz-good)' : 'var(--viz-warning)'
  const circumference = Math.PI * r
  return <div className="flex flex-col items-center">
    <svg viewBox="0 0 180 100" className="w-full max-w-[15rem]" role="img" aria-label={`ROAS ${ratio(roas)}, break-even ${breakEven}×`}>
      <path d={arc(max)} fill="none" stroke="var(--viz-empty)" strokeWidth={14} strokeLinecap="round" />
      <path d={arc(max)} fill="none" stroke={color} strokeWidth={14} strokeLinecap="round"
        strokeDasharray={circumference} strokeDashoffset={circumference * (1 - shown / max)}
        className="motion-safe:transition-[stroke-dashoffset] motion-safe:duration-[1400ms] motion-safe:ease-out" />
      <line x1={bx} y1={by} x2={90 + (bx - 90) * 1.22} y2={86 + (by - 86) * 1.22} stroke="hsl(var(--foreground))" strokeWidth={2} />
      <text x={90 + (bx - 90) * 1.32} y={86 + (by - 86) * 1.32} textAnchor="middle" className="fill-muted-foreground text-[8px]">{breakEven}×</text>
      <text x={90} y={78} textAnchor="middle" className="fill-foreground font-serif text-[22px] font-semibold">{spend ? ratio(roas) : '—'}</text>
    </svg>
    <p className="mt-1 flex items-center gap-1.5 text-xs">
      <Icon className="h-3.5 w-3.5" style={{ color }} aria-hidden />
      {spend ? (above ? 'Above' : 'Below') + ` break-even (${breakEven}×)` : 'No spend in this window'}
    </p>
    <p className="mt-0.5 text-[11px] text-muted-foreground">{money(value, currency)} purchase value on {money(spend, currency)} spend</p>
  </div>
}

/** From impression to purchase, with the rate at each step. Sources differ, so each is named. */
function Funnel({ o }: { o: Overview }) {
  const steps = [
    { label: 'Impressions', value: o.totals.cur.impressions, source: 'Google Ads' },
    { label: 'Clicks', value: o.totals.cur.clicks, source: 'Google Ads' },
    { label: 'Ad checkouts', value: o.store.cur.adCheckouts, source: 'store, from an ad click' },
    { label: 'Purchases', value: o.totals.cur.purchases, source: 'attributed by Google' },
  ]
  return <ol className="relative space-y-0">
    {steps.map((s, i) => {
      const prev = steps[i - 1]
      const rate = prev && prev.value ? s.value / prev.value : null
      return <li key={s.label} className="viz-rise" style={{ animationDelay: `${i * 90}ms` }}>
        {i > 0 && <div className="flex items-center gap-2 py-1 pl-[7px]">
          <span className="h-5 w-px bg-border" aria-hidden />
          <span className="rounded-full border bg-muted/60 px-2 py-0.5 text-[11px] tabular-nums text-muted-foreground">{rate == null ? '—' : pct(rate, rate < 0.01 ? 2 : 1)}</span>
        </div>}
        <div className="flex items-baseline justify-between gap-3">
          <span className="flex items-center gap-2 text-sm">
            <span className="h-3.5 w-3.5 rounded-full border-2" style={{ borderColor: SPEND, background: i === steps.length - 1 && s.value ? SPEND : 'transparent' }} aria-hidden />
            {s.label}
          </span>
          <span className="font-serif text-xl font-semibold tabular-nums">{num(s.value)}</span>
        </div>
        <p className="pl-[22px] text-[11px] text-muted-foreground">{s.source}</p>
      </li>
    })}
  </ol>
}

export function OverviewPage() {
  const sel = useSelection()
  const q = useOverview(sel.account?.customerId, sel.days)
  const strategy = useStrategy()
  const o = q.data
  const cur = o?.account.currency || sel.account?.currency || 'USD'
  const whole = (v: number) => money(v, cur, 0)
  const cents = (v: number) => money(v, cur)

  const revenueByDate = new Map(o?.store.series.map(r => [r.date, r.revenue]) || [])
  const spendVsRevenue = (o?.series || []).map(r => ({ day: r.date, cost: r.cost, revenue: revenueByDate.get(r.date) ?? 0 })) as unknown as Day[]
  const lines: ChartSeries[] = [{ key: 'cost', label: 'Ad spend', color: SPEND }, { key: 'revenue', label: 'Store revenue', color: REVENUE }]
  const totalSpend = o?.campaigns.reduce((n, c) => n + c.cur.cost, 0) || 0

  return (
    <GrowthFrame
      title="Growth"
      description="Paid performance per market against the store's own orders, with every campaign one click from its product groups, search terms and products. Campaign numbers are live from Google Ads (refreshed every 10 minutes); window ends yesterday in the account's time zone."
    >
      {q.error && <ErrorNote error={q.error} />}
      {!o ? (
        <div className="grid gap-4 md:grid-cols-4">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-28" />)}</div>
      ) : (
        <>
          <div className="text-xs text-muted-foreground">
            {shortDate(o.window.from)} – {shortDate(o.window.to)} vs {shortDate(o.window.prevFrom)} – {shortDate(o.window.prevTo)} ·
            read {formatDistanceToNow(new Date(o.readAt), { addSuffix: true })} · today so far: {money(o.totals.today.cost, cur)} spend,{' '}
            {num(o.totals.today.clicks)} clicks, {num(o.totals.today.purchases)} purchases
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi label="Ad spend" value={money(o.totals.cur.cost, cur)} amount={o.totals.cur.cost} format={cents} cur={o.totals.cur.cost} prev={o.totals.prev.cost} good="none"
              spark={{ values: o.series.map(r => r.cost), color: SPEND }}
              hint={<span>{money(o.totals.budget, cur, 0)}/day budget · {pct(o.totals.cur.cost / (o.totals.budget * o.window.days || 1), 0)} used</span>} />
            <Kpi label="Clicks" value={num(o.totals.cur.clicks)} amount={o.totals.cur.clicks} format={num} cur={o.totals.cur.clicks} prev={o.totals.prev.clicks} delay={50}
              spark={{ values: o.series.map(r => r.clicks), color: SPEND }}
              hint={<span>CTR {pct(o.totals.cur.ctr, 2)} · {num(o.totals.cur.impressions)} impr.</span>} />
            <Kpi label="Avg. CPC" value={o.totals.cur.clicks ? money(o.totals.cur.cpc, cur) : '—'} cur={o.totals.cur.cpc} prev={o.totals.prev.cpc} good="down" delay={100} />
            <Kpi label="Ad checkouts" value={num(o.store.cur.adCheckouts)} amount={o.store.cur.adCheckouts} format={num} cur={o.store.cur.adCheckouts} prev={o.store.prev.adCheckouts} delay={150}
              spark={{ values: o.store.series.map(r => r.adCheckouts), color: REVENUE }}
              hint={<span>checkouts started from an ad click</span>} />
            <Kpi label="Google purchases" value={num(o.totals.cur.purchases)} amount={o.totals.cur.purchases} format={num} cur={o.totals.cur.purchases} prev={o.totals.prev.purchases} delay={200}
              hint={<span>{o.totals.cur.cpa != null ? `${money(o.totals.cur.cpa, cur)} per purchase` : 'Purchase (server), attributed by Google'}</span>} />
            <Kpi label="ROAS" value={o.totals.cur.cost ? ratio(o.totals.cur.roas) : '—'} cur={o.totals.cur.roas} prev={o.totals.prev.roas} delay={250}
              tone={o.totals.cur.cost && o.totals.cur.roas >= o.breakEvenRoas ? 'ok' : o.totals.cur.cost > 0 ? 'warn' : undefined}
              hint={<span>break-even {o.breakEvenRoas}× · {money(o.totals.cur.value, cur)} value</span>} />
            <Kpi label="Store orders" value={num(o.store.cur.orders)} amount={o.store.cur.orders} format={num} cur={o.store.cur.orders} prev={o.store.prev.orders} delay={300}
              spark={{ values: o.store.series.map(r => r.orders), color: REVENUE }}
              hint={<span>{money(o.store.cur.revenue, cur)} revenue · all channels, {o.account.currency}</span>} />
            <Kpi label="Blended MER" value={o.store.blended.cur.mer != null ? ratio(o.store.blended.cur.mer) : '—'} delay={350}
              cur={o.store.blended.cur.mer} prev={o.store.blended.prev.mer}
              hint={<span>store revenue ÷ ad spend{o.store.blended.cur.costPerOrder != null ? ` · ${money(o.store.blended.cur.costPerOrder, cur)}/order` : ''}</span>} />
          </div>

          <div className="grid items-start gap-4 xl:grid-cols-3">
            <Card className="viz-rise xl:col-span-2">
              <CardHeader className="pb-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <CardTitle className="text-base">Spend and store revenue</CardTitle>
                    <CardDescription>Per day, both in {cur} on one axis. Store revenue is every channel, so the gap is what the whole store earns per ad dollar, not what ads alone drove.</CardDescription>
                  </div>
                  <Legend series={lines} />
                </div>
              </CardHeader>
              <CardContent>
                <AreaChart data={spendVsRevenue} series={lines} format={whole} tooltipFormat={cents} height={280} label="Daily ad spend and store revenue" />
              </CardContent>
            </Card>
            <Card className="viz-rise" style={{ animationDelay: '80ms' }}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Return and funnel</CardTitle>
                <CardDescription>This window, Google-attributed.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <RoasGauge roas={o.totals.cur.roas} breakEven={o.breakEvenRoas} spend={o.totals.cur.cost} currency={cur} value={o.totals.cur.value} />
                <Funnel o={o} />
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <DayBars title="Clicks" rows={o.series} value={(r) => r.clicks} format={(v) => num(v)} color={SPEND} />
            <DayBars title="Store orders" rows={o.store.series} value={(r) => r.orders} format={(v) => num(v)} color={REVENUE} />
          </div>

          <Card className="viz-rise">
            <CardHeader>
              <CardTitle className="text-base">Campaigns</CardTitle>
              <CardDescription>Impression share is how many of the auctions the campaign qualified for it showed in; &ldquo;rank&rdquo; losses mean the bid (or product quality) was too low, &ldquo;budget&rdquo; losses mean it ran out of money.</CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Campaign</TableHead>
                    <TableHead className="text-right">Budget/day</TableHead>
                    <TableHead className="text-right">Spend</TableHead>
                    <TableHead className="text-right">Clicks</TableHead>
                    <TableHead className="text-right">CTR</TableHead>
                    <TableHead className="text-right">CPC</TableHead>
                    <TableHead className="text-right">Purchases</TableHead>
                    <TableHead className="text-right">ROAS</TableHead>
                    <TableHead>Impression share</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {o.campaigns.map((c) => {
                    const m = metricCells(c.cur, cur)
                    const share = totalSpend ? c.cur.cost / totalSpend : 0
                    return (
                      <TableRow key={c.id}>
                        <TableCell>
                          <Link to={`/app/growth/campaigns/${c.id}${sel.qs()}`} className="font-medium text-foreground hover:underline">{c.name}</Link>
                          <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[11px] text-muted-foreground">
                            <StatusPill status={c.status} /> {c.channel.toLowerCase().replace('_', ' ')} · {c.bidding.toLowerCase().replace(/_/g, ' ')}
                            {c.today.cost > 0 && <> · today {money(c.today.cost, cur)}</>}
                          </div>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{money(c.budget, cur, 0)}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {m.cost}
                          <div className="ml-auto mt-1 h-1 w-16 overflow-hidden rounded-full" style={{ background: 'var(--viz-empty)' }} title={`${pct(share, 0)} of spend`}>
                            <div className="viz-grow-x h-full rounded-full" style={{ width: `${share * 100}%`, background: SPEND }} />
                          </div>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{m.clicks}</TableCell>
                        <TableCell className="text-right tabular-nums">{m.ctr}</TableCell>
                        <TableCell className="text-right tabular-nums">{m.cpc}</TableCell>
                        <TableCell className="text-right tabular-nums">{m.purchases}</TableCell>
                        <TableCell className="text-right tabular-nums">{m.roas}</TableCell>
                        <TableCell><ShareBar won={c.share.impressionShare} rank={c.share.lostToRank} budget={c.share.lostToBudget} /></TableCell>
                      </TableRow>
                    )
                  })}
                  {!o.campaigns.length && (
                    <TableRow><TableCell colSpan={9} className="text-center text-sm text-muted-foreground">No campaigns enabled or spending in this account.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}

      <Card className="viz-rise">
        <CardHeader>
          <CardTitle className="text-base">Growth plan</CardTitle>
          <CardDescription>
            Each step and the evidence it waits for — the same gates as the daily email. A gate opening never acts on its own; it says the next move is ours.{' '}
            <Link to={`/app/growth/strategy${sel.qs()}`} className="underline">Signals and detail →</Link>
          </CardDescription>
        </CardHeader>
        <CardContent>
          {strategy.error ? <ErrorNote error={strategy.error} /> : strategy.data ? <PlanList steps={strategy.data.plan} /> : <Skeleton className="h-32" />}
        </CardContent>
      </Card>
    </GrowthFrame>
  )
}
